import { useState } from 'react';
import { propertyListings } from '../../../mocks/propertyListings';

type Filter = 'all' | 'available' | 'regional' | 'metro';

export default function RentalsGrid() {
  const [filter, setFilter] = useState<Filter>('all');

  const filtered = propertyListings.filter((p) => {
    if (filter === 'available') return p.availableNow;
    if (filter === 'regional') return p.region === 'regional';
    if (filter === 'metro') return p.region === 'metro';
    return true;
  });

  const filterLabels: { key: Filter; label: string }[] = [
    { key: 'all', label: 'All Listings' },
    { key: 'available', label: 'Available Now' },
    { key: 'regional', label: 'Regional' },
    { key: 'metro', label: 'Metro Cities' },
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Filter bar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
          <div>
            <p className="text-navy-700/50 text-sm">{filtered.length} {filtered.length === 1 ? 'property' : 'properties'} {filter === 'available' ? 'available' : 'listed'}</p>
          </div>
          <div className="flex items-center bg-navy-50 rounded-full p-1" style={{ background: 'rgb(240, 244, 252)' }}>
            {filterLabels.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setFilter(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  filter === key
                    ? 'bg-navy-800 text-white'
                    : 'text-navy-700/60 hover:text-navy-800'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          {filtered.map((property) => (
            <div
              key={property.id}
              className="bg-white rounded-lg border border-navy-100 overflow-hidden hover:border-gold-300 hover:-translate-y-1 transition-all duration-200 group"
            >
              {/* Image */}
              <div className="relative w-full h-52 overflow-hidden">
                <img
                  src={property.image}
                  alt={property.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                {/* Tag */}
                <div
                  className={`absolute top-3 left-3 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${
                    property.availableNow
                      ? 'bg-emerald-500 text-white'
                      : 'bg-navy-800 text-white/80'
                  }`}
                >
                  {property.tag}
                </div>
                {/* Region badge */}
                <div className={`absolute top-3 right-3 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap ${
                  property.region === 'regional'
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-blue-100 text-blue-800'
                }`}>
                  {property.region === 'regional' ? 'Regional' : 'Metro'}
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-semibold text-navy-800 text-base leading-snug mb-1">{property.title}</h3>
                    <div className="flex items-center gap-1.5 text-navy-700/55 text-sm">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-line text-xs" />
                      </div>
                      {property.suburb}, {property.state}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-heading font-bold text-xl text-navy-800">${property.weeklyRent}</p>
                    <p className="text-navy-700/45 text-xs">per week</p>
                  </div>
                </div>

                {/* Room details */}
                <div className="flex items-center gap-4 py-3 border-t border-b border-navy-50 mb-4 text-navy-700/60 text-sm">
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-hotel-bed-line text-sm" />
                    </div>
                    {property.bedType}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-drop-line text-sm" />
                    </div>
                    {property.bathrooms} Bath
                  </div>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-1.5 mb-5">
                  {property.features.map((feat) => (
                    <span
                      key={feat}
                      className="text-navy-700 text-xs px-2.5 py-1 rounded-full whitespace-nowrap"
                      style={{ background: 'rgba(27,42,74,0.07)' }}
                    >
                      {feat}
                    </span>
                  ))}
                </div>

                <a
                  href="https://rentals.galaxyhousing.com.au"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-full flex items-center justify-center gap-2 py-3 rounded text-sm font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${
                    property.availableNow
                      ? 'bg-gold-500 hover:bg-gold-400 text-navy-900'
                      : 'bg-navy-100 text-navy-500 cursor-not-allowed'
                  }`}
                >
                  {property.availableNow ? (
                    <>
                      Enquire Now
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </div>
                    </>
                  ) : (
                    'Join Waitlist'
                  )}
                </a>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
